/**
 * FormField — label + control + hint + error, wired for screen readers.
 *
 * Used by: every form in the Admin app (chi nhánh, sản phẩm, vai trò, thiết
 * bị…), the POS discount dialog and the Staff leave-request form.
 *
 * It owns the id/aria plumbing so no screen has to remember it:
 *   - the label's `htmlFor` matches the control's `id`;
 *   - `aria-describedby` points at the hint AND the error when both exist;
 *   - `aria-invalid` is set from `error`, which is what a screen reader
 *     announces — a red border alone communicates nothing to a blind user.
 *
 * The control is supplied via a render prop so this component works with an
 * Input, a Select, a Textarea or anything React Hook Form registers.
 */
import { useId } from 'react';
import type { ReactNode } from 'react';
import { cn } from '../utils.js';

export interface FieldControlProps {
  readonly id: string;
  readonly 'aria-describedby': string | undefined;
  readonly 'aria-invalid': boolean;
}

export interface FormFieldProps {
  readonly label: string;
  /** Shown as a red asterisk AND announced via the label text. */
  readonly required?: boolean;
  readonly hint?: ReactNode;
  readonly error?: string | null | undefined;
  readonly className?: string;
  /** Lay the label beside the control (used in dense settings screens). */
  readonly inline?: boolean;
  readonly children: (props: FieldControlProps) => ReactNode;
}

export function FormField({
  label,
  required = false,
  hint,
  error,
  className,
  inline = false,
  children,
}: FormFieldProps): React.JSX.Element {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const hasError = typeof error === 'string' && error !== '';

  const describedBy =
    [hint !== undefined && hint !== null ? hintId : null, hasError ? errorId : null].filter(Boolean).join(' ') ||
    undefined;

  return (
    <div className={cn(inline ? 'flex items-center gap-4' : 'flex flex-col gap-1.5', className)}>
      <label htmlFor={id} className={cn('text-sm font-medium text-fg', inline && 'w-48 shrink-0')}>
        {label}
        {required ? (
          <span className="ml-1 text-danger-fg" aria-hidden="true">
            *
          </span>
        ) : null}
        {required ? <span className="sr-only"> (bắt buộc)</span> : null}
      </label>

      <div className={cn(inline && 'flex-1')}>
        {children({ id, 'aria-describedby': describedBy, 'aria-invalid': hasError })}

        {hint !== undefined && hint !== null ? (
          <p id={hintId} className="mt-1 text-xs text-fg-subtle">
            {hint}
          </p>
        ) : null}

        {hasError ? (
          <p id={errorId} role="alert" className="mt-1 text-xs font-medium text-danger-fg">
            {error}
          </p>
        ) : null}
      </div>
    </div>
  );
}
